import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useGetSecurityQuestionsStore, usePostSecurityQuestionsStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { RoutePaths } from '@shared/utils';
import { Input, Modal, PageLayout } from '@shared/widgets';
import { Select } from '@shared/widgets/select';
import { Button } from '@shared/widgets';

export interface IFormData {
  answer1: string;
  answer2: string;
  answer3: string;
  question1: string;
  question2: string;
  question3: string;
}

export const SecurityAnswersPage = () => {
  const formControl = useForm<IFormData>({
    mode: 'onChange',
    defaultValues: { answer1: '', answer2: '', answer3: '', question1: '', question2: '', question3: '' },
  });

  const question1 = formControl.watch('question1');
  const question2 = formControl.watch('question2');
  const question3 = formControl.watch('question3');
  const answer2 = formControl.watch('answer2');
  const answer3 = formControl.watch('answer3');

  const getSecurityQuestionsStore = useGetSecurityQuestionsStore();
  const postSecurityQuestionsStore = usePostSecurityQuestionsStore();

  const [resultText, setResultText] = useState('Security questions were updated successfully.');
  const [showResult, setShowResult] = useState(false);

  const { navigate } = useRouteHook();

  useEffect(() => {
    getSecurityQuestionsStore.get();
  }, []);

  const onSubmit = useCallback(async (data: IFormData) => {
    try {
      await postSecurityQuestionsStore.post({
        ...data,
        question2: data.question2 || 'none',
        question3: data.question3 || 'none',
      });
      setResultText('Security questions were updated successfully.');
    } catch {
      setResultText('Something went wrong.');
    } finally {
      setShowResult(true);
    }
  }, []);

  const filteredQuestions = useMemo(
    () => getSecurityQuestionsStore.data?.filter((question) => question !== 'What is the name of your best friend?'),
    [getSecurityQuestionsStore.data],
  );

  const questionsOptions = useMemo(
    () =>
      filteredQuestions.map((item, i) => (
        <option value={item} key={i}>
          {item}
        </option>
      )),
    [getSecurityQuestionsStore.data],
  );
  const questions2Options = useMemo(
    () =>
      filteredQuestions.map((item, idx) => (
        <option value={item} disabled={item === question1} key={idx}>
          {item}
        </option>
      )),
    [question1, getSecurityQuestionsStore.data],
  );
  const questions3Options = useMemo(
    () =>
      filteredQuestions.map((item, idx) => (
        <option value={item} disabled={item === question1 || item === question2} key={idx}>
          {item}
        </option>
      )),
    [question1, question2, getSecurityQuestionsStore.data],
  );

  return (
    <PageLayout>
      <div className='flex flex-col items-center px-5 py-[65px]'>
        <div className='mb-[68px] flex flex-col items-center'>
          <h1 className='mb-[28px] font-[Prata] text-[48px] text-[#000]'>Security Questions</h1>
          <div className='text-center text-xl font-light text-[#272727]'>
            A security question is a word or phrase that only you know and that serves as the key to your personal world.
          </div>
        </div>
        <form className='relative mx-auto flex max-w-[401px] flex-col items-stretch gap-4' onSubmit={formControl.handleSubmit(onSubmit)}>
          <Controller
            name='question1'
            control={formControl.control}
            rules={{ required: 'This field is required' }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Select {...field} wrapperClassName='!border-[#272727] !bg-[#f6f6f6] !h-[44px]'>
                  <option value='' disabled>
                    Secret question 1
                  </option>
                  {questionsOptions}
                </Select>
                {error && <span className='text-[#bf2828]'>{error.message}</span>}
              </div>
            )}
          />
          <Controller
            name='answer1'
            control={formControl.control}
            rules={{ required: 'This field is required' }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input {...field} inputClassName='max-md:!border-[#272727]' />
                {error && <span className='text-[#bf2828]'>{error.message}</span>}
              </div>
            )}
          />
          <Controller
            name='question2'
            control={formControl.control}
            rules={{ required: answer2 ? 'This field is required' : undefined }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Select {...field} wrapperClassName='!border-[#272727] !bg-[#f6f6f6] !h-[44px]'>
                  <option value=''>Secret question 2</option>
                  {questions2Options}
                </Select>
                {error && <span className='text-[#bf2828]'>{error.message}</span>}
              </div>
            )}
          />
          <Controller
            name='answer2'
            control={formControl.control}
            rules={{ required: question2 ? 'This field is required' : undefined }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input {...field} inputClassName='max-md:!border-[#272727]' />
                {error && <span className='text-[#bf2828]'>{error.message}</span>}
              </div>
            )}
          />
          <Controller
            name='question3'
            control={formControl.control}
            rules={{ required: answer3 ? 'This field is required' : undefined }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Select {...field} wrapperClassName='!border-[#272727] !bg-[#f6f6f6] !h-[44px]'>
                  <option value=''>Secret question 3</option>
                  {questions3Options}
                </Select>
                {error && <span className='text-[#bf2828]'>{error.message}</span>}
              </div>
            )}
          />
          <Controller
            name='answer3'
            control={formControl.control}
            rules={{ required: question3 ? 'This field is required' : undefined }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input {...field} inputClassName='max-md:!border-[#272727]' />
                {error && <span className='text-[#bf2828]'>{error.message}</span>}
              </div>
            )}
          />
          <Button className='mt-2' fullWidth isLoading={postSecurityQuestionsStore.loading} type='submit'>
            Save
          </Button>
        </form>
      </div>
      <Modal title='Security questions' isOpen={showResult} onClose={() => navigate(RoutePaths.profile.onNavigate())}>
        <p className='max-md:text-[#4c4c4c80]'>{resultText}</p>
        {!postSecurityQuestionsStore.success && (
          <div className='mt-4 max-md:mt-4 max-md:rounded-lg max-md:bg-[#d7c29a1a] max-md:p-2'>
            <p className='text-[#000]'>Please contact our support team</p>
            <p className='text-[#000]'>24-hour phone support:</p>
            <p className='text-[#000]'>+44-203-151-7000 / +800-800-00-700</p>
          </div>
        )}
        <Button className='mx-auto mt-9 w-[159px] max-md:w-full' onClick={() => navigate(RoutePaths.profile.onNavigate())}>
          Ok
        </Button>
      </Modal>
    </PageLayout>
  );
};
